import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { LandingBlock, LandingBlockType } from '../../types';
import { SOCIAL_NETWORK_OPTIONS } from '../../data/socialNetworks';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { Button } from '../ui/Button';
interface Field {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'url' | 'toggle' | 'select';
  options?: {
    value: string;
    label: string;
  }[];
}
type ItemKind = 'benefit' | 'testimonial' | 'faq' | 'stat' | 'logo' | 'feature' | 'plan' | null;
const SCHEMA: Record<LandingBlockType, {
  fields: Field[];
  items: ItemKind;
}> = {
  topbar: {
    fields: [{
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }, {
      key: 'showLogo',
      label: 'Mostrar logo',
      kind: 'toggle'
    }, {
      key: 'logoAlign',
      label: 'Alineación del logo',
      kind: 'select',
      options: [{
        value: 'left',
        label: 'A la izquierda'
      }, {
        value: 'center',
        label: 'Centrado'
      }]
    }, {
      key: 'logoHeight',
      label: 'Altura del logo (px)',
      kind: 'text'
    }],
    items: null
  },
  logo: {
    fields: [{
      key: 'logoHeight',
      label: 'Altura del logo (px)',
      kind: 'text'
    }],
    items: null
  },
  hero: {
    fields: [{
      key: 'eyebrow',
      label: 'Etiqueta superior',
      kind: 'text'
    }, {
      key: 'brandLine',
      label: 'Línea de marca (ATRAE · FILTRA · CONVIERTE)',
      kind: 'text'
    }, {
      key: 'title',
      label: 'Título',
      kind: 'textarea'
    }, {
      key: 'highlight',
      label: 'Palabras resaltadas en azul',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }, {
      key: 'subtitleHighlight',
      label: 'Palabras resaltadas del subtítulo',
      kind: 'text'
    }, {
      key: 'extraLine',
      label: 'Línea complementaria',
      kind: 'textarea'
    }, {
      key: 'showLogo',
      label: 'Mostrar logo sobre el título',
      kind: 'toggle'
    }, {
      key: 'logoHeight',
      label: 'Altura del logo (px)',
      kind: 'text'
    }],
    items: null
  },
  titulo: {
    fields: [{
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }],
    items: null
  },
  subtitulo: {
    fields: [{
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }],
    items: null
  },
  imagen: {
    fields: [{
      key: 'url',
      label: 'URL de la imagen',
      kind: 'url'
    }, {
      key: 'alt',
      label: 'Texto alternativo',
      kind: 'text'
    }],
    items: null
  },
  video: {
    fields: [{
      key: 'url',
      label: 'URL del vídeo (YouTube, Vimeo, Wistia o mp4)',
      kind: 'url'
    }, {
      key: 'thumbnailUrl',
      label: 'Miniatura',
      kind: 'url'
    }, {
      key: 'overlayTitle',
      label: 'Texto sobre la miniatura',
      kind: 'textarea'
    }, {
      key: 'overlayHighlight',
      label: 'Palabras resaltadas del texto superpuesto',
      kind: 'text'
    }, {
      key: 'note',
      label: 'Anotación manuscrita',
      kind: 'text'
    }, {
      key: 'caption',
      label: 'Texto bajo el vídeo',
      kind: 'textarea'
    }, {
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }],
    items: null
  },
  beneficios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'benefit'
  },
  estadisticas: {
    fields: [],
    items: 'stat'
  },
  testimonios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'testimonial'
  },
  logos_clientes: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }],
    items: 'logo'
  },
  formulario: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }, {
      key: 'showProgress',
      label: 'Mostrar barra de progreso',
      kind: 'toggle'
    }, {
      key: 'showPrivacy',
      label: 'Mostrar aviso de privacidad',
      kind: 'toggle'
    }],
    items: null
  },
  faq: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }],
    items: 'faq'
  },
  calendario: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }, {
      key: 'calendarSource',
      label: 'Calendario',
      kind: 'select',
      options: [{
        value: 'global',
        label: 'Google Calendar principal de la empresa'
      }, {
        value: 'custom',
        label: 'Calendario propio de esta landing'
      }]
    }, {
      key: 'url',
      label: 'URL de reserva propia (Calendly, Google Calendar o cualquier agenda)',
      kind: 'url'
    }, {
      key: 'calendarMode',
      label: 'Cómo se muestra',
      kind: 'select',
      options: [{
        value: 'boton',
        label: 'Botón que abre el calendario'
      }, {
        value: 'incrustado',
        label: 'Calendario incrustado en la página'
      }]
    }, {
      key: 'embedHeight',
      label: 'Altura del calendario incrustado (px)',
      kind: 'number'
    }, {
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }],
    items: null
  },
  whatsapp: {
    fields: [{
      key: 'number',
      label: 'Número de WhatsApp',
      kind: 'text'
    }, {
      key: 'message',
      label: 'Mensaje inicial',
      kind: 'text'
    }],
    items: null
  },
  cta_final: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }, {
      key: 'ctaLabel',
      label: 'Texto del botón',
      kind: 'text'
    }, {
      key: 'whatsappNumber',
      label: 'Número de WhatsApp (si se rellena, el botón abre el chat)',
      kind: 'text'
    }, {
      key: 'whatsappMessage',
      label: 'Mensaje inicial de WhatsApp',
      kind: 'text'
    }],
    items: null
  },
  footer: {
    fields: [{
      key: 'text',
      label: 'Texto legal',
      kind: 'textarea'
    }],
    items: 'logo'
  },
  pilares: {
    fields: [],
    items: 'benefit'
  },
  frase: {
    fields: [{
      key: 'text',
      label: 'Frase destacada',
      kind: 'textarea'
    }],
    items: null
  },
  servicios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }],
    items: 'benefit'
  },
  caracteristicas: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'feature'
  },
  galeria: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }],
    items: 'logo'
  },
  precios: {
    fields: [{
      key: 'title',
      label: 'Título de la sección',
      kind: 'text'
    }, {
      key: 'subtitle',
      label: 'Subtítulo',
      kind: 'textarea'
    }],
    items: 'plan'
  },
  contacto: {
    fields: [{
      key: 'title',
      label: 'Título',
      kind: 'text'
    }, {
      key: 'text',
      label: 'Texto',
      kind: 'textarea'
    }, {
      key: 'email',
      label: 'Email',
      kind: 'text'
    }, {
      key: 'phone',
      label: 'Teléfono',
      kind: 'text'
    }, {
      key: 'address',
      label: 'Dirección',
      kind: 'text'
    }],
    items: null
  }
};
const ITEM_TEMPLATE: Record<string, Record<string, string>> = {
  benefit: {
    icon: 'Target',
    title: 'Nuevo beneficio',
    text: 'Describe el beneficio.'
  },
  testimonial: {
    name: '',
    role: '',
    company: '',
    result: '',
    text: '',
    avatar: ''
  },
  faq: {
    q: '',
    a: ''
  },
  stat: {
    value: '',
    label: ''
  },
  feature: {
    title: 'Nueva característica',
    text: 'Describe la característica.'
  },
  plan: {
    name: 'Nuevo plan',
    price: '0 €',
    period: 'al mes',
    features: 'Ventaja 1\nVentaja 2',
    featured: '',
    ctaLabel: 'Empezar'
  }
};
const ITEM_FIELDS: Record<string, {
  key: string;
  label: string;
}[]> = {
  benefit: [{
    key: 'icon',
    label: 'Icono'
  }, {
    key: 'title',
    label: 'Título'
  }, {
    key: 'text',
    label: 'Texto'
  }],
  testimonial: [{
    key: 'name',
    label: 'Nombre'
  }, {
    key: 'role',
    label: 'Cargo'
  }, {
    key: 'company',
    label: 'Empresa'
  }, {
    key: 'result',
    label: 'Resultado conseguido'
  }, {
    key: 'text',
    label: 'Testimonio'
  }, {
    key: 'avatar',
    label: 'Foto (URL)'
  }],
  faq: [{
    key: 'q',
    label: 'Pregunta'
  }, {
    key: 'a',
    label: 'Respuesta'
  }],
  stat: [{
    key: 'value',
    label: 'Cifra'
  }, {
    key: 'label',
    label: 'Etiqueta'
  }],
  feature: [{
    key: 'title',
    label: 'Título'
  }, {
    key: 'text',
    label: 'Descripción'
  }],
  plan: [{
    key: 'name',
    label: 'Nombre del plan'
  }, {
    key: 'price',
    label: 'Precio'
  }, {
    key: 'period',
    label: 'Periodo'
  }, {
    key: 'features',
    label: 'Ventajas (una por línea)'
  }, {
    key: 'featured',
    label: 'Destacado (escribe "sí")'
  }, {
    key: 'ctaLabel',
    label: 'Texto del botón'
  }]
};
const ICON_OPTIONS = ['Magnet', 'Filter', 'TrendingUp', 'Target', 'Users', 'Bot', 'Video', 'Megaphone', 'Sparkles', 'BarChart3', 'Building2', 'CalendarDays', 'MessageCircle', 'Briefcase'].map(value => ({
  value,
  label: value
}));

/** Campos de lista que se editan con área de texto. */
const MULTILINE_KEYS = ['text', 'a', 'features'];
interface BlockSettingsProps {
  block: LandingBlock;
  onChange: (block: LandingBlock) => void;
}
interface SocialLink {
  network: string;
  url: string;
}
const NETWORK_OPTIONS = SOCIAL_NETWORK_OPTIONS;

/**
 * Redes sociales del footer. Una red sin URL no se muestra en la landing, y si
 * ninguna tiene URL el bloque de iconos desaparece por completo.
 */
function SocialsEditor({
  block,
  onChange
}: BlockSettingsProps) {
  const socials = (block.config.socials ?? []) as SocialLink[];
  function update(next: SocialLink[]) {
    onChange({
      ...block,
      config: {
        ...block.config,
        socials: next
      }
    });
  }
  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= socials.length) return;
    const next = [...socials];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  }
  return <div className="space-y-2" data-id="element-258" data-mp="27j7nqc2jjk9e">
      <p className="text-sm font-medium text-foreground" data-id="element-259" data-mp="110rm1u5qf5e2">Redes sociales</p>
      <p className="text-xs text-muted-foreground" data-id="element-260" data-mp="10yokccv7vnti">
        Deja la URL vacía para ocultar una red. Los enlaces se abren en una pestaña
        nueva.
      </p>
      {socials.map((social, index) => <div key={`social-${index}`} className="space-y-3 rounded-lg border border-border bg-surface p-3" data-id="element-261" data-mp="lvwxdgituab6" data-mpi={index}>
          <Select id={`${block.id}-social-${index}`} label="Red social" options={NETWORK_OPTIONS} value={social.network} onChange={e => {
        const next = [...socials];
        next[index] = {
          ...social,
          network: e.target.value
        };
        update(next);
      }} data-id="element-262" data-mp="qurmt1baux49" />
          <Input id={`${block.id}-social-url-${index}`} label="URL" type="url" placeholder="https://instagram.com/empresaoficial" value={social.url} onChange={e => {
        const next = [...socials];
        next[index] = {
          ...social,
          url: e.target.value
        };
        update(next);
      }} data-id="element-263" data-mp="14qvdikmysnbz" />
          <div className="flex items-center gap-1" data-id="element-264" data-mp="3gebh25kksbq8">
            <Button variant="ghost" size="sm" aria-label="Subir" onClick={() => move(index, -1)} data-id="element-265" data-mp="3gfmmsaxkztjz">
              <ArrowUpIcon className="h-4 w-4" data-id="element-266" data-mp="3ldodni190x60" />
            </Button>
            <Button variant="ghost" size="sm" aria-label="Bajar" onClick={() => move(index, 1)} data-id="element-267" data-mp="3dbnkmu8mo7c5">
              <ArrowDownIcon className="h-4 w-4" data-id="element-268" data-mp="38e5l1ugi34gt" />
            </Button>
            <Button variant="ghost" size="sm" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-270" data-mp="3nbeectpd6ib6" />} onClick={() => update(socials.filter((_, i) => i !== index))} data-id="element-269" data-mp="3namq63y3jmux">
              Eliminar
            </Button>
          </div>
        </div>)}
      <Button variant="secondary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-272" data-mp="3u5cq9yq903" />} onClick={() => update([...socials, {
      network: 'instagram',
      url: ''
    }])} data-id="element-271" data-mp="559ir7blirq">
        Añadir red social
      </Button>
    </div>;
}
export function BlockSettings({
  block,
  onChange
}: BlockSettingsProps) {
  const schema = SCHEMA[block.type];
  function setConfig(key: string, value: unknown) {
    onChange({
      ...block,
      config: {
        ...block.config,
        [key]: value
      }
    });
  }
  const listKey = block.type === 'footer' || block.type === 'logos_clientes' ? 'links' : 'items';
  const rawItems = (block.config[block.type === 'footer' ? 'links' : 'items'] ?? []) as unknown[];
  function setItems(next: unknown[]) {
    setConfig(block.type === 'footer' ? 'links' : 'items', next);
  }
  return <div className="space-y-4" data-id="element-273" data-mp="ohnjfqpza50s">
      {schema.fields.map(field => field.kind === 'toggle' ? <Switch key={field.key} id={`${block.id}-${field.key}`} label={field.label} checked={String(block.config[field.key] ?? 'si') !== 'no'} onChange={checked => setConfig(field.key, checked ? 'si' : 'no')} data-id="element-274" data-mp="tggz9psqm844" /> : field.kind === 'select' ? <Select key={field.key} id={`${block.id}-${field.key}`} label={field.label} options={field.options ?? []} value={String(block.config[field.key] ?? field.options?.[0]?.value ?? '')} onChange={e => setConfig(field.key, e.target.value)} data-id="element-275" data-mp="jkndrgx1j4a7" /> : field.kind === 'textarea' ? <Textarea key={field.key} id={`${block.id}-${field.key}`} label={field.label} rows={3} value={String(block.config[field.key] ?? '')} onChange={e => setConfig(field.key, e.target.value)} data-id="element-276" data-mp="3s073i5yi64ki" /> : <Input key={field.key} id={`${block.id}-${field.key}`} label={field.label} type={field.kind === 'url' ? 'url' : 'text'} value={String(block.config[field.key] ?? '')} onChange={e => setConfig(field.key, e.target.value)} data-id="element-277" data-mp="136ipc2uj26n9" />)}

      <Select id={`${block.id}-spacing`} label="Espaciado" options={[{
      value: 'compacto',
      label: 'Compacto'
    }, {
      value: 'normal',
      label: 'Normal'
    }, {
      value: 'amplio',
      label: 'Amplio'
    }]} value={block.spacing} onChange={e => onChange({
      ...block,
      spacing: e.target.value as LandingBlock['spacing']
    })} data-id="element-278" data-mp="uce2rvmffhdj" />

      {/* Listas simples de texto (logos, enlaces del footer) */}
      {(schema.items === 'logo' || block.type === 'footer') && <div className="space-y-2" data-id="element-279" data-mp="6hb9nbglarr9">
          <p className="text-sm font-medium text-foreground" data-id="element-280" data-mp="6icpdt8jw2fb">
            {block.type === 'footer' ? 'Enlaces legales' : block.type === 'galeria' ? 'URLs de las imágenes' : 'Nombres de empresa'}
          </p>
          {(rawItems as string[]).map((value, index) => <div key={`${listKey}-${index}`} className="flex gap-2" data-id="element-281" data-mp="28cqwyb9djlcb" data-mpi={index}>
              <Input id={`${block.id}-item-${index}`} value={value} onChange={e => {
          const next = [...(rawItems as string[])];
          next[index] = e.target.value;
          setItems(next);
        }} data-id="element-282" data-mp="2dc3i8rklkkcs" />
              <Button variant="ghost" size="sm" aria-label="Eliminar" onClick={() => setItems(rawItems.filter((_, i) => i !== index))} data-id="element-283" data-mp="2n1epe8nxbey1">
                <Trash2Icon className="h-4 w-4" data-id="element-284" data-mp="1pgis38k9xchj" />
              </Button>
            </div>)}
          <Button variant="secondary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-286" data-mp="1zd3tzl0zy7h5" />} onClick={() => setItems([...(rawItems as string[]), ''])} data-id="element-285" data-mp="1ueap2gzrzosl">
            Añadir
          </Button>
        </div>}

      {/* Redes sociales del footer: URL, orden y visibilidad */}
      {block.type === 'footer' && <SocialsEditor block={block} onChange={onChange} data-id="element-287" data-mp="3arhdkdopw0rd" />}

      {/* Listas de objetos */}
      {schema.items && schema.items !== 'logo' && <div className="space-y-3" data-id="element-288" data-mp="1fougszhm1eh8">
          <p className="text-sm font-medium text-foreground" data-id="element-289" data-mp="1fo2u6e5zqr33">Elementos</p>
          {(rawItems as Record<string, string>[]).map((item, index) => <div key={`item-${index}`} className="space-y-3 rounded-lg border border-border bg-surface p-3" data-id="element-290" data-mp="1aphwomi4ilic" data-mpi={index}>
              {ITEM_FIELDS[schema.items as string].map(field => field.key === 'icon' ? <Select key={field.key} id={`${block.id}-${index}-${field.key}`} label={field.label} options={ICON_OPTIONS} value={item[field.key] ?? ''} onChange={e => {
          const next = [...(rawItems as Record<string, string>[])];
          next[index] = {
            ...item,
            [field.key]: e.target.value
          };
          setItems(next);
        }} data-id="element-291" data-mp="1jrr1qwumhmoc" /> : MULTILINE_KEYS.includes(field.key) ? <Textarea key={field.key} id={`${block.id}-${index}-${field.key}`} label={field.label} rows={3} value={item[field.key] ?? ''} onChange={e => {
          const next = [...(rawItems as Record<string, string>[])];
          next[index] = {
            ...item,
            [field.key]: e.target.value
          };
          setItems(next);
        }} data-id="element-292" data-mp="10yev7jla84ij" /> : <Input key={field.key} id={`${block.id}-${index}-${field.key}`} label={field.label} value={item[field.key] ?? ''} onChange={e => {
          const next = [...(rawItems as Record<string, string>[])];
          next[index] = {
            ...item,
            [field.key]: e.target.value
          };
          setItems(next);
        }} data-id="element-293" data-mp="2op49xrg1o1ng" />)}
              <Button variant="ghost" size="sm" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-295" data-mp="37nyp31jf7445" />} onClick={() => setItems(rawItems.filter((_, i) => i !== index))} data-id="element-294" data-mp="33n53ietga1ef">
                Eliminar elemento
              </Button>
            </div>)}
          <Button variant="secondary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-297" data-mp="2ym8whsdsxz4s" />} onClick={() => setItems([...rawItems, {
        ...ITEM_TEMPLATE[schema.items as string]
      }])} data-id="element-296" data-mp="2ynjz3ovih0ub">
            Añadir elemento
          </Button>
        </div>}
    </div>;
}